import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Panel } from "@/components/ui/panel";
import { PageSkeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/status/status-badge";
import { api } from "@/lib/api";
import { formatUnix } from "./shared";

type DashboardData = {
  checkedAt: number;
  organizations: { total: number; active: number; cancelled: number };
  activeStreams: number;
  billingStatusCounts: Record<string, number>;
  openFailures: number;
  attendeesThisPeriod: number;
  userStatusCounts: Record<string, number>;
};

export function SuperAdminDashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api<DashboardData>("/api/platform/dashboard")
      .then(setData)
      .catch((err) =>
        setError(
          err instanceof Error ? err.message : "Failed to load dashboard",
        ),
      );
  }, []);

  if (error) {
    return (
      <div className="space-y-4">
        <h1 className="text-xl font-semibold text-rs-ink">Dashboard</h1>
        <p className="text-sm text-rs-danger">{error}</p>
      </div>
    );
  }

  if (!data) return <PageSkeleton />;

  const metrics = [
    {
      label: "Active Accounts",
      value: data.organizations.active,
      to: "/superadmin/accounts",
    },
    {
      label: "Active Streams",
      value: data.activeStreams,
      to: "/superadmin/streams",
    },
    {
      label: "Open Failures",
      value: data.openFailures,
      to: "/superadmin/failures",
    },
    {
      label: "Attendees This Period",
      value: data.attendeesThisPeriod,
      to: "/superadmin/usage",
    },
    {
      label: "Trialing Accounts",
      value: data.billingStatusCounts.trialing ?? 0,
      to: "/superadmin/subscriptions",
    },
    {
      label: "Paid Active",
      value: data.billingStatusCounts.active ?? 0,
      to: "/superadmin/subscriptions",
    },
    {
      label: "Past Due",
      value: data.billingStatusCounts.past_due ?? 0,
      to: "/superadmin/subscriptions",
    },
    {
      label: "Suspended Users",
      value: data.userStatusCounts.suspended ?? 0,
      to: "/superadmin/users?status=suspended",
    },
  ];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-rs-ink">Dashboard</h1>
        <p className="text-sm text-rs-ink-muted">
          Platform summary across all accounts. Checked{" "}
          {formatUnix(data.checkedAt)}.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.map((m) => (
          <Panel key={m.label} className="p-4">
            <p className="text-sm text-rs-ink-muted">{m.label}</p>
            <p className="mt-1 text-2xl font-semibold text-rs-ink">
              {m.value.toLocaleString()}
            </p>
            <Link
              className="mt-2 inline-block text-sm text-rs-accent hover:underline"
              to={m.to}
            >
              View
            </Link>
          </Panel>
        ))}
      </div>
      <Panel className="p-5">
        <h2 className="font-semibold text-rs-ink">Billing Snapshot</h2>
        <ul className="mt-3 flex flex-wrap gap-2">
          {Object.entries(data.billingStatusCounts).map(([status, count]) => (
            <li key={status}>
              <StatusBadge
                tone={
                  status === "past_due"
                    ? "warning"
                    : status === "active"
                      ? "syncing"
                      : "neutral"
                }
              >
                {status}: {count}
              </StatusBadge>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}
